import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { Tabs } from '@zillow/constellation';
import { SearchBox, buildURL } from '@zillow/search-box';
import { event } from '@zillow/universal-analytics';
import PropTypes from 'prop-types';
import React, { useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import triggerTabClickEvent from '../../utils/clickstream/tab-click.js';

var CategoryTabList = /*#__PURE__*/styled(Tabs.List).withConfig({
  componentId: "sc-shrp8h-0"
})(["border-bottom-color:transparent;"]);
var CategoryTab = /*#__PURE__*/styled(Tabs.Tab).withConfig({
  componentId: "sc-shrp8h-1"
})(["background-color:transparent !important;color:inherit !important;&[aria-selected='false']:before{content:unset !important;}"]);
function getAction(category, origin, searchSelection, location) {
  var _searchSelection$data, _searchSelection$data2;
  switch (category) {
    case 'buy':
      var buyPath = buildURL(searchSelection, location, 'for_sale');
      return "" + origin + buyPath;
    case 'rent':
      var rentPath = buildURL(searchSelection, location, 'for_rent');
      return "" + origin + rentPath;
    case 'sell':
      if (!(searchSelection !== null && searchSelection !== void 0 && (_searchSelection$data = searchSelection.data) !== null && _searchSelection$data !== void 0 && _searchSelection$data.zpid)) {
        throw new Error("Could not find zpid for sell category");
      }
      return origin + "/sell/options/address-verification/?zpid=" + searchSelection.data.zpid + "&source=" + window.location.origin + window.location.pathname;
    case 'zestimate':
      if (!(searchSelection !== null && searchSelection !== void 0 && (_searchSelection$data2 = searchSelection.data) !== null && _searchSelection$data2 !== void 0 && _searchSelection$data2.zpid)) {
        throw new Error("Could not find zpid for zestimate category");
      }
      return origin + "/myzillow/yourhome/" + searchSelection.data.zpid;
    default:
      throw new Error("Unknown category of \"" + category + "\"");
  }
}
var createSearchBoxApolloClient = function createSearchBoxApolloClient(uri) {
  if (uri === void 0) {
    uri = 'https://zg-graph-dev.int.zgcp-federated-nonprod-k8s.zg-int.net/graphql/';
  }
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: new HttpLink({
      uri: uri,
      credentials: 'same-origin',
      headers: {
        'x-caller-id': 'zrs-cms'
      }
    }),
    cache: new InMemoryCache()
  });
};
var PropertySearch = function PropertySearch(_ref) {
  var _ref$propertySearchCo = _ref.propertySearchConfigValues,
    origin = _ref$propertySearchCo.origin,
    graphURL = _ref$propertySearchCo.graphURL,
    placeholder = _ref.placeholder,
    _ref$categories = _ref.categories,
    categories = _ref$categories === void 0 ? ['buy', 'rent', 'sell', 'zestimate'] : _ref$categories,
    size = _ref.size,
    id = _ref.id,
    t = _ref.t,
    opensInNewWindow = _ref.opensInNewWindow;
  var _useState = useState(categories[0]),
    _useState2 = _slicedToArray(_useState, 2),
    category = _useState2[0],
    setCategory = _useState2[1];
  var inputRef = useRef(null);
  var zgGraphClient = useMemo(function () {
    return createSearchBoxApolloClient(graphURL);
  }, [graphURL]);
  var boxProps = {
    abTests: {
      SXP_SEARCH_BOX_URL_ADDRESS: 'BDP_HDP_SRP'
    },
    inputRef: inputRef,
    handleSubmit: function handleSubmit(value, location) {
      var action = getAction(category, origin, value, location);

      // Open in new foreground window. Disable as it doesn't see `window`.
      var url = new URL(action);

      // Append T param if its set.
      if (t) {
        url.searchParams.append('t', t);
      }
      // eslint-disable-next-line no-undef
      window.open(url.href, opensInNewWindow ? '_blank' : '_self');
      event({
        // https://dataportal.zillowgroup.net/event-registry/2055
        envelope: {
          event_type_id: '2055',
          event_type_version_id: '1',
          event_template_id: '126',
          event_template_version_id: '1'
          // Master ID: 2055.1.126.1
        },
        clickstream_trigger: {
          trigger_type_nm: 'interaction',
          trigger_location_nm: 'cms',
          trigger_source_nm: 'cms_button',
          trigger_object_nm: 'no_trigger_object'
        },
        semantic: {
          semantic_event_nm: 'cms_submit'
        },
        cms_info: {
          block_id: id || '',
          block_type_cd: 'zg_property_search',
          block_target_url: action,
          block_displayed_txt: placeholder,
          form_business_line_txt: category,
          form_submit_label: 'magnifying glass icon'
        }
      });
    },
    recentSearchLocalStorageKey: 'recentSearchesEnhanced',
    fetchSavedHomes: true,
    fetchSavedSearches: true,
    platform: 'CCMS',
    filterSuggestions: ['sell', 'zestimate'].includes(category) ? function (suggestions) {
      return suggestions.filter(function (suggestion) {
        return suggestion.category === 'ADDRESS';
      });
    } : undefined,
    platformConfig: {
      CCMS: {
        placeholderText: placeholder,
        supportsLocationIcon: false
      }
    },
    size: size,
    // Only suggest addresses if sell or zestimate is selected (don't suggest regions)
    resultTypes: ['sell', 'zestimate'].includes(category) ? ['ADDRESSES'] : ['REGIONS', 'FORSALE', 'RENTALS', 'SOLD'],
    zgGraphClient: zgGraphClient
  };

  // Order here is the order it will show the tabs in.
  var categoryOptions = [{
    value: 'buy',
    label: 'Buy'
  }, {
    value: 'rent',
    label: 'Rent'
  }, {
    value: 'sell',
    label: 'Sell'
  }, {
    value: 'zestimate',
    label: 'Zestimate'
  }].filter(function (categoryOption) {
    return categories.includes(categoryOption.value);
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, categoryOptions.length > 1 && /*#__PURE__*/React.createElement(CategoryTabList, {
    id: id,
    "aria-label": "Property Search Categories",
    defaultSelected: categoryOptions[0].value,
    onSelected: function onSelected(value) {
      setCategory(value);
      var categoryOption = categoryOptions.find(function (option) {
        return option.value === value;
      });
      if (!categoryOption) {
        throw new Error("Could not find category option for value of \"" + value + "\"");
      }
      triggerTabClickEvent({
        label: categoryOption.label,
        id: id + "-" + value
      });

      // Hack to reset the value of the search input to ''
      var nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      // eslint-disable-next-line no-undef
      window.HTMLInputElement.prototype, 'value').set;
      if (!inputRef.current) {
        return;
      }
      nativeInputValueSetter.call(inputRef.current, '');

      // eslint-disable-next-line no-undef
      var inputEvent = new Event('input', {
        bubbles: true
      });
      inputRef.current.dispatchEvent(inputEvent);
    }
  }, categoryOptions.map(function (_ref2) {
    var value = _ref2.value,
      label = _ref2.label;
    return /*#__PURE__*/React.createElement(CategoryTab, {
      key: value,
      id: value
    }, label);
  })), /*#__PURE__*/React.createElement(SearchBox, boxProps));
};
PropertySearch.propTypes = process.env.NODE_ENV !== "production" ? {
  propertySearchConfigValues: PropTypes.shape({
    graphURL: PropTypes.string,
    origin: PropTypes.string
  }).isRequired,
  placeholder: PropTypes.string,
  // Must have at least one
  categories: PropTypes.arrayOf(PropTypes.oneOf(['buy', 'rent', 'sell', 'zestimate'])).isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  id: PropTypes.string.isRequired,
  t: PropTypes.string,
  opensInNewWindow: PropTypes.bool
} : {};
PropertySearch.defaultProps = {
  placeholder: 'Enter an address, neighborhood, city, or ZIP code',
  size: 'md',
  t: '',
  opensInNewWindow: true
};

/** @component */
var PropertySearch$1 = PropertySearch;

export { PropertySearch$1 as default };
